import { useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { FormRow } from "@/components/admin/form";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { statusOrderV2 } from "@/lib/status";

/**
 * Étapes qu'un opérateur fait franchir à un lot de commandes.
 *
 * On ne propose que les étapes de la marche avant du traitement : annuler,
 * rembourser ou ouvrir un litige se décide commande par commande, avec ses
 * justificatifs — jamais sur une sélection entière.
 */
const ETAPES = [
	"PAID",
	"CONFIRMED",
	"PROCURING",
	"IN_HUB",
	"READY_TO_SHIP",
	"SHIPPING",
	"DELIVERED",
	"COMPLETED",
] as const;

interface Props {
	count: number;
	onClose: () => void;
	onConfirm: (status: string) => Promise<void>;
}

/**
 * Avancement groupé de commandes.
 *
 * Remplace une fenêtre système qui demandait de SAISIR le code du statut —
 * « ex: CONFIRMED, READY_TO_SHIP » — au clavier et sans faute. Une faute de
 * frappe et l'action échouait sans rien dire ; un code oublié et l'opérateur
 * devait aller le chercher ailleurs.
 */
export function BulkStatusModal({ count, onClose, onConfirm }: Props) {
	const [statut, setStatut] = useState("");
	const [envoi, setEnvoi] = useState(false);
	const [erreur, setErreur] = useState<string | null>(null);

	const valider = async () => {
		if (!statut) {
			setErreur("Choisissez l'étape à appliquer.");
			return;
		}
		setErreur(null);
		setEnvoi(true);
		try {
			await onConfirm(statut);
			onClose();
		} catch (e) {
			setErreur(extractErrorMessage(e, "Avancement impossible"));
		} finally {
			setEnvoi(false);
		}
	};

	return (
		<Modal
			title="Faire avancer les commandes"
			subtitle={`${count} commande${count > 1 ? "s" : ""} sélectionnée${count > 1 ? "s" : ""}`}
			onClose={onClose}
			width={440}
		>
			<FormRow
				label="Nouvelle étape"
				required
				helper="Les commandes dont l'étape actuelle ne permet pas ce passage seront refusées par le serveur."
			>
				<select
					className="aform-i"
					value={statut}
					onChange={(e) => setStatut(e.target.value)}
					data-testid="bulk-status"
				>
					<option value="">— Choisir une étape —</option>
					{ETAPES.map((s) => (
						<option key={s} value={s}>
							{statusOrderV2(s).l}
						</option>
					))}
				</select>
			</FormRow>

			{erreur && (
				<div
					role="alert"
					style={{
						marginBottom: 14,
						fontSize: 12.5,
						color: "var(--a-danger)",
					}}
				>
					{erreur}
				</div>
			)}

			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose} disabled={envoi}>
					Revenir
				</AdminButton>
				<AdminButton
					variant="primary"
					onClick={valider}
					disabled={envoi || !statut}
					data-testid="bulk-status-confirm"
				>
					{envoi ? "Application…" : "Appliquer"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
